import type {
    CreateProgressReportRequest,
    EmotionField,
    PhysicalField,
    ProgressReport,
    SymptomFrequency,
} from './progress-reports';

/**
 * What the form holds while it is being filled in, which is deliberately not
 * the same shape as what gets submitted.
 *
 * Every answer starts as `null` rather than a default, so "not answered yet" is
 * distinguishable from a real answer. A 1-10 slider defaulting to 5 would make
 * an untouched question look answered, which is the same class of bug as an
 * empty weight box reading as zero.
 */
export type ProgressReportFormValues = {
    energyLevel: number | null;
    sleepQuality: number | null;

    painDescription: string;
    painLevel: number | null;

    negativeFoodReactions: string;

    /** Kept as a string: a number input yields strings, and '' is not 0. */
    weightKg: string;
    /** Decision M made explicit. See buildSubmitPayload. */
    weightUnavailable: boolean;

    commitmentLevel: number | null;
    questionsOrRemarks: string;
} & Record<PhysicalField | EmotionField, SymptomFrequency | null>;

export type NumericScaleField = 'energyLevel' | 'sleepQuality' | 'painLevel' | 'commitmentLevel';
export type FrequencyField = PhysicalField | EmotionField;

/**
 * Turns the form's in-progress shape into the wire payload.
 *
 * Throws if a required answer is missing. That is not defensive noise: it means
 * the caller skipped validation, and submitting a half-report to a clinical
 * record is worse than a crash in dev.
 *
 * `weightKg` is OMITTED, never sent as 0 or '', when the client said they could
 * not weigh themselves. The backend stores `dto.weightKg ?? null`, so omitting
 * is what makes the stored value a real null. P4 draws a missing weight as
 * missing, and it can only do that if this distinction survives.
 */
export function buildSubmitPayload(values: ProgressReportFormValues): CreateProgressReportRequest {
    const require = <T>(value: T | null, field: string): T => {
        if (value === null || value === undefined) {
            throw new Error(`Cannot submit: ${field} is unanswered`);
        }
        return value;
    };

    const frequencies = {} as Record<FrequencyField, SymptomFrequency>;
    for (const key of FREQUENCY_FIELDS) {
        frequencies[key] = require(values[key], key);
    }

    const payload: CreateProgressReportRequest = {
        energyLevel: require(values.energyLevel, 'energyLevel'),
        sleepQuality: require(values.sleepQuality, 'sleepQuality'),
        ...frequencies,
        // The backend requires the key but accepts an empty string: "nothing to
        // describe" is an answer, not a missing one.
        painDescription: values.painDescription.trim(),
        painLevel: require(values.painLevel, 'painLevel'),
        negativeFoodReactions: values.negativeFoodReactions.trim(),
        commitmentLevel: require(values.commitmentLevel, 'commitmentLevel'),
    };

    const weight = values.weightKg.trim();
    if (!values.weightUnavailable && weight !== '') {
        payload.weightKg = Number(weight);
    }

    const remarks = values.questionsOrRemarks.trim();
    if (remarks !== '') {
        payload.questionsOrRemarks = remarks;
    }

    return payload;
}

/**
 * A stored report back into the form's shape, so a client who already sent this
 * week's report edits their own answers rather than starting blank.
 *
 * The inverse of `buildSubmitPayload`, and the two have to stay inverses: a
 * missing weight comes back as the "I cannot weigh myself" tick rather than as
 * an empty box, because that is the distinction the box alone cannot carry and
 * re-submitting an untouched form must not quietly change it.
 */
export function toFormValues(report: ProgressReport): ProgressReportFormValues {
    const frequencies = {} as Record<FrequencyField, SymptomFrequency | null>;
    for (const key of FREQUENCY_FIELDS) {
        frequencies[key] = report[key] ?? null;
    }

    return {
        ...EMPTY_FORM_VALUES,
        ...frequencies,
        energyLevel: report.energyLevel,
        sleepQuality: report.sleepQuality,
        painDescription: report.painDescription ?? '',
        painLevel: report.painLevel,
        negativeFoodReactions: report.negativeFoodReactions ?? '',
        // A Decimal arrives as a string ("159.7"), which is already what the
        // number input wants. Never parsed and re-formatted: that is how 159.7
        // becomes 159.70000000000002 in a clinical record.
        weightKg: report.weightKg ?? '',
        weightUnavailable: report.weightKg === null,
        commitmentLevel: report.commitmentLevel,
        questionsOrRemarks: report.questionsOrRemarks ?? '',
    };
}

const FREQUENCY_FIELDS: readonly FrequencyField[] = [
    'troubleFallingAsleep',
    'wakeDuringNight',
    'needCaffeineToGetGoing',
    'mentalFog',
    'sinusitisOrPostNasalDrip',
    'heartburnOrAcidReflux',
    'bloatingOrFlatulence',
    'constipation',
    'diarrhoea',
    'headachesOrMigraines',
    'otherPain',
    'anxious',
    'depressed',
    'stressed',
    'content',
    'peacefulAndCalm',
    'compassionWithMyself',
    'thankfulForMyLife',
    'optimisticAboutMyProgress',
];

export const EMPTY_FORM_VALUES: ProgressReportFormValues = {
    energyLevel: null,
    sleepQuality: null,
    troubleFallingAsleep: null,
    wakeDuringNight: null,
    needCaffeineToGetGoing: null,
    mentalFog: null,
    sinusitisOrPostNasalDrip: null,
    heartburnOrAcidReflux: null,
    bloatingOrFlatulence: null,
    constipation: null,
    diarrhoea: null,
    headachesOrMigraines: null,
    otherPain: null,
    painDescription: '',
    painLevel: null,
    negativeFoodReactions: '',
    anxious: null,
    depressed: null,
    stressed: null,
    content: null,
    peacefulAndCalm: null,
    compassionWithMyself: null,
    thankfulForMyLife: null,
    optimisticAboutMyProgress: null,
    weightKg: '',
    weightUnavailable: false,
    commitmentLevel: null,
    questionsOrRemarks: '',
};
